"use strict";

import { getDurationStatus } from "../../utils/generalFunctions.js";

export const getSpeakerDisplayName = (characters = [], speaker) => {
    const safeCharacters = Array.isArray(characters) ? characters : [];
    const result = safeCharacters.find((char) => char.speaker === speaker);

    return result?.displayName || speaker;
};

export const findTranslationByLanguageCode = (translations = [], languageCode) => {
    const safeTranslations = Array.isArray(translations) ? translations : [];

    return safeTranslations.find(
        (translation) => translation?.languageCode === languageCode,
    );
};

export const createEmptyTranslation = (languageCode) => ({
    languageCode,
    translatedText: "",
    translatedAudioList: [],
    translatedAudio: "",
    translatedAudioRating: "",
    translatedAudioNote: null,
    translatedAudioPath: null,
    translatedRecording: "",
});

export const ensureProjectFinalLanguageTranslations = (
    translations = [],
    finalLanguageCodes = [],
) => {
    const safeTranslations = Array.isArray(translations) ? translations : [];
    const updatedTranslations = [...safeTranslations];
    const existingLanguageCodes = safeTranslations.map(
        (translation) => translation?.languageCode,
    );

    (finalLanguageCodes || []).forEach((languageCode) => {
        if (!existingLanguageCodes.includes(languageCode)) {
            updatedTranslations.push(createEmptyTranslation(languageCode));
        }
    });

    return updatedTranslations;
};

export const getDefaultVoiceIdForEmotion = (
    characters = [],
    speaker,
    originalAudioEmotion,
) => {
    const safeCharacters = Array.isArray(characters) ? characters : [];
    const character = safeCharacters.find((char) => char.speaker === speaker);
    const characterVoice = character?.voices?.find(
        (voice) => voice.emotion === originalAudioEmotion,
    );

    return characterVoice?.voice_id || "";
};

export const getTranslationDurationMessage = (originalLength, translatedLength) => {
    const status = getDurationStatus(originalLength, translatedLength);

    if (status === "perfect") {
        return { label: "Perfect", legacyLabel: "Duration Perfect", color: "inherit" };
    }
    if (status === "needsLonger") {
        return { label: "Make It Longer", legacyLabel: "Make It Longer", color: "red" };
    }
    if (status === "needsShorter") {
        return { label: "Make It Shorter", legacyLabel: "Make It Shorter", color: "red" };
    }

    return null;
};

export const getRecommendedSpeedOption = (originalLength, translatedLength) => {
    const status = getDurationStatus(originalLength, translatedLength);

    if (status !== "needsLonger" && status !== "needsShorter") {
        return null;
    }

    const speedOptions = [0.8, 0.9, 1.0, 1.1, 1.2];
    const rankedOptions = speedOptions
        .map((speedOption) => {
            const adjustedDuration = translatedLength / speedOption;
            const adjustedStatus = getDurationStatus(originalLength, adjustedDuration);
            const absoluteDurationDifference = Math.abs(
                adjustedDuration - originalLength,
            );
            const stillWarning =
                adjustedStatus === "needsLonger" || adjustedStatus === "needsShorter";

            return {
                speedOption,
                adjustedDuration,
                absoluteDurationDifference,
                stillWarning,
            };
        })
        .sort((a, b) => {
            if (a.stillWarning !== b.stillWarning) {
                return a.stillWarning ? 1 : -1;
            }
            if (a.absoluteDurationDifference !== b.absoluteDurationDifference) {
                return a.absoluteDurationDifference - b.absoluteDurationDifference;
            }
            return Math.abs(a.speedOption - 1.0) - Math.abs(b.speedOption - 1.0);
        });

    return rankedOptions[0]?.speedOption ?? null;
};

export const findDialogueInTimeRange = (dialogues = [], time) => {
    const safeDialogues = Array.isArray(dialogues) ? dialogues : [];

    return safeDialogues.find(
        (dialogue) => time >= dialogue.start && time < dialogue.end,
    );
};
